import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import {
  Landmark, RefreshCw, BadgeCheck, FileSignature, GraduationCap, Scale, Users,
  RotateCw, Bell, ExternalLink, Briefcase, ArrowLeft, AlertTriangle, ClipboardCheck,
  CheckCircle2,
} from 'lucide-react';

const OFFICIAL_NOTICE =
  'INeedASocialWorker.com provides licensing information and links to official state agencies. ' +
  'Applications, renewals, payments, and final license decisions are completed through the applicable licensing board.';

const ACTIONS = [
  { key: 'apply', title: 'APPLY', sub: 'for a License', Icon: FileSignature, urlKey: 'apply_url', cta: 'Continue to Official Application' },
  { key: 'renew', title: 'RENEW', sub: 'Your License', Icon: RefreshCw, urlKey: 'renewal_url', cta: 'Continue to Official Renewal Portal' },
  { key: 'verify', title: 'VERIFY', sub: 'a License', Icon: BadgeCheck, urlKey: 'verify_url', cta: 'Verify on Official State Website' },
  { key: 'board', title: 'VISIT', sub: 'Licensing Board', Icon: Landmark, urlKey: 'board_url', cta: 'Continue to Licensing Board' },
];

const buildContent = (action, data, licenseType) => {
  const st = data.name;
  const lt = licenseType || 'your license level';
  switch (action) {
    case 'apply':
      return {
        heading: `Apply for a ${lt} in ${st}`,
        intro: `Here is what you'll generally need to apply for ${lt} licensure in ${st}. Requirements vary by license level — always confirm current details on the official board site before you begin.`,
        checklist: [
          `Hold the required degree for ${lt} from a CSWE-accredited program (or state-approved equivalent).`,
          `Review ${st}'s specific education and coursework requirements.`,
          'Register for and pass the appropriate ASWB examination (see ASWB Exam Information below).',
          'Complete the official state application and pay the application fee.',
          'Submit official transcripts and any required background check / fingerprints.',
          'Provide supervised-experience documentation (required for clinical-level licensure).',
        ],
      };
    case 'renew':
      return {
        heading: `Renew your ${lt} in ${st}`,
        intro: `Use this checklist to prepare your ${st} ${lt} renewal. Your site can also save your expiration date and send reminders.`,
        checklist: [
          'Confirm your license expiration date and the state renewal deadline.',
          `Complete required continuing education: ${data.ce || 'see your state board for current CE hours.'}`,
          `Complete required ethics hours: ${data.ethics || 'see your state board for current ethics requirements.'}`,
          'Resolve any outstanding fees or disciplinary requirements.',
          'Log in to the official renewal portal and pay the renewal fee.',
          'Keep proof of CE completion in case of audit.',
        ],
      };
    case 'verify':
      return {
        heading: `Verify a ${st} social work license`,
        intro: `Use ${st}'s official public verification system to confirm a license. For legal confirmation, always rely on the official board (or ASWB) verification result.`,
        checklist: [
          'Have the licensee\'s full name and/or license number ready.',
          'Open the official state verification system.',
          'You may also cross-check through the ASWB license verification tool below.',
        ],
      };
    default: // board
      return {
        heading: `${st} Licensing Board`,
        intro: `Visit the official ${data.board} website for forms, fees, rules, and contact information.`,
        checklist: [
          'Review current rules, fees, and forms directly on the board website.',
          'Look for the board\'s newsletters or rule updates for any recent changes.',
        ],
      };
  }
};

const Section = ({ Icon, title, children }) => (
  <div className="bg-white rounded-2xl border p-5" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center"><Icon className="w-4 h-4 text-primary" /></div>
      <h2 className="font-serif text-lg font-bold text-primary">{title}</h2>
    </div>
    <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
  </div>
);

const StateLicense = () => {
  const { state: slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [licenseType, setLicenseType] = useState('');
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/license-center/states/${slug}`);
      setData(r.data.state); setRelated(r.data.related);
      setLicenseType((r.data.state.license_levels && r.data.state.license_levels[0]) || '');
      document.title = `${r.data.state.name} Social Work License & Renewal | I Need A Social Worker`;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
      m.content = `${r.data.state.name} social work licensing: ${r.data.state.board}, application & renewal links, CE and ethics requirements, clinical supervision, ASWB exam info, reciprocity, and license verification.`;
    } catch (e) { setNotFound(true); } finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen pt-28 text-center text-gray-500">Loading…</div>;
  if (notFound || !data) return (
    <div className="min-h-screen pt-28 text-center px-4">
      <p className="text-gray-600 font-medium">State not found.</p>
      <Link to="/license-renewal-center"><Button className="mt-4 bg-secondary hover:bg-secondary-hover text-white">Back to License Center</Button></Link>
    </div>
  );

  const activeAction = ACTIONS.find((a) => a.key === action);
  const content = action ? buildContent(action, data, licenseType) : null;
  const continueUrl = activeAction ? data[activeAction.urlKey] : null;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <button onClick={() => navigate('/license-renewal-center')} className="text-secondary text-sm hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All States</button>
          <div className="flex items-center gap-3 mt-3">
            <span className="w-12 h-12 rounded-full bg-white text-primary font-bold flex items-center justify-center">{data.abbr}</span>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold">{data.name} Social Work Licensing</h1>
          </div>
          <p className="text-white/80 mt-2">{data.board}</p>
        </div>
      </section>

      {/* Interactive: License Type -> Action -> Instructions + Checklist -> Continue */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div className="bg-white rounded-2xl border shadow-sm p-5 sm:p-6" data-testid="license-flow-panel">
          {/* Step 1: license type */}
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Step 1 — Choose your license type</p>
            <div className="flex flex-wrap gap-2" data-testid="license-type-chips">
              {data.license_levels.map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setLicenseType(lvl)}
                  className={`px-3 py-2 rounded-full text-sm font-medium border transition-all ${licenseType === lvl ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200 hover:border-secondary'}`}
                  data-testid={`license-type-${lvl.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: action */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Step 2 — What would you like to do?</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ACTIONS.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAction(a.key)}
                  className={`rounded-xl p-4 text-center border transition-all ${action === a.key ? 'bg-primary text-white border-primary' : 'bg-[#F7F5EF] text-gray-800 border-gray-200 hover:border-secondary'}`}
                  data-testid={`action-${a.key}`}
                >
                  <a.Icon className={`w-6 h-6 mx-auto ${action === a.key ? 'text-secondary' : 'text-primary'}`} />
                  <p className="font-bold text-sm mt-1">{a.title}</p>
                  <p className={`text-xs ${action === a.key ? 'text-white/70' : 'text-gray-500'}`}>{a.sub}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3: instructions + checklist */}
          {content && (
            <div className="mt-5 border-t pt-5" data-testid="action-instructions">
              <h2 className="font-serif text-xl font-bold text-primary">{content.heading}</h2>
              <p className="text-sm text-gray-600 mt-1">{content.intro}</p>
              <ul className="mt-4 space-y-2">
                {content.checklist.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {action === 'renew' && (
                <div className="mt-4 bg-secondary/10 border border-secondary/30 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm text-primary flex items-center gap-2"><Bell className="w-4 h-4 text-secondary" /> Want a reminder before this expires?</p>
                  <Button size="sm" onClick={() => navigate(isAuthenticated ? `/my-license-reminders?state=${slug}` : '/login')} className="bg-primary hover:bg-primary/90 text-white" data-testid="renew-reminder-btn">
                    {isAuthenticated ? 'Save my renewal date' : 'Sign in to save'}
                  </Button>
                </div>
              )}

              {action === 'verify' && (
                <a href={data.aswb_verify_url} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1 mt-3 text-xs"><ExternalLink className="w-3 h-3" /> ASWB license verification</a>
              )}

              {continueUrl && (
                <a href={continueUrl} target="_blank" rel="noreferrer">
                  <Button className="mt-5 bg-secondary hover:bg-secondary-hover text-white" data-testid="continue-official-btn">
                    {activeAction.cta} <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          )}

          {/* Official-agency notice (always under the buttons) */}
          <div className="mt-5 bg-gray-50 border border-gray-200 rounded-xl p-3 flex gap-2 text-xs text-gray-600" data-testid="official-notice">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-gray-400 mt-0.5" />
            <p>{OFFICIAL_NOTICE}</p>
          </div>
        </div>
      </section>

      {/* Reference sections */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-2 gap-4">
        <Section Icon={ClipboardCheck} title="License Levels">
          <ul className="list-disc ml-4 space-y-1">{data.license_levels.map((l) => <li key={l}>{l}</li>)}</ul>
        </Section>
        <Section Icon={GraduationCap} title="Continuing Education">{data.ce}</Section>
        <Section Icon={Scale} title="Ethics Requirements">{data.ethics}</Section>
        <Section Icon={Users} title="Clinical Supervision">{data.supervision}</Section>
        <Section Icon={FileSignature} title="ASWB Exam Information">
          {data.exam}
          <a href={data.aswb_verify_url} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1 mt-2 text-xs"><ExternalLink className="w-3 h-3" /> ASWB license verification</a>
        </Section>
        <Section Icon={RotateCw} title="Reciprocity & Endorsement">{data.reciprocity}</Section>
      </div>

      {/* Renewal reminder */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-primary rounded-2xl p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Bell className="w-8 h-8 text-secondary flex-shrink-0" />
            <div>
              <h2 className="font-serif text-xl font-bold">Never miss your {data.name} renewal</h2>
              <p className="text-white/80 text-sm">Get reminders 90, 60, 30, and 7 days before your license expires.</p>
            </div>
          </div>
          <Button onClick={() => navigate(isAuthenticated ? `/my-license-reminders?state=${slug}` : '/login')} className="bg-secondary hover:bg-secondary-hover text-white flex-shrink-0" data-testid="state-reminder-btn">
            {isAuthenticated ? 'Set a Renewal Reminder' : 'Sign In to Set Reminder'}
          </Button>
        </div>
      </div>

      {/* Related resources */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <h2 className="font-serif text-xl font-bold text-primary mb-3">Related Resources in {data.name}</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to={`/jobs?location=${encodeURIComponent(related.jobs_query)}`} className="bg-white rounded-xl border p-4 hover:border-secondary hover:shadow-sm transition-all" data-testid="related-jobs">
            <Briefcase className="w-5 h-5 text-primary" />
            <p className="font-semibold text-sm text-primary mt-2">Find Social Work Jobs</p>
            <p className="text-xs text-gray-500">in {data.name}</p>
          </Link>
          <Link to={`/find-a-supervisor?state=${related.supervisors_state}`} className="bg-white rounded-xl border p-4 hover:border-secondary hover:shadow-sm transition-all" data-testid="related-supervisors">
            <Users className="w-5 h-5 text-primary" />
            <p className="font-semibold text-sm text-primary mt-2">Find Clinical Supervisors</p>
            <p className="text-xs text-gray-500">in {data.name}</p>
          </Link>
          <Link to={`/internships?state=${related.internships_state}`} className="bg-white rounded-xl border p-4 hover:border-secondary hover:shadow-sm transition-all" data-testid="related-internships">
            <GraduationCap className="w-5 h-5 text-primary" />
            <p className="font-semibold text-sm text-primary mt-2">Find Internships</p>
            <p className="text-xs text-gray-500">in {data.name}</p>
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-800">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600" />
          <p>{data.disclaimer}</p>
        </div>
      </div>
    </div>
  );
};

export default StateLicense;
